import { LockIcon } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useScanCadence } from '@/hooks/useScanCadence';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import {
  buildSentence,
  DEFAULT_SENTENCE,
} from '@/sections/settings/components/forms/scanScheduleSentence';
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import { ScanScheduleWizard } from '@/sections/settings/components/modals/ScanScheduleWizard';
import { SettingCompactCard } from '@/sections/settings/components/primitives/SettingCompactCard';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';

export interface ScanScheduleCardProps {
  /** Email for impersonation - if provided, fetches/writes for this user. */
  email?: string;
  /** Whether edit is enabled at all (default true). Combined with Praetorian gate. */
  editable?: boolean;
}

/**
 * Merged "Scan Schedule" card (collapsed) — FIDELITY §A/§B.
 *
 * Replaces today's standalone "Schedule" card at position #5 in /settings/scan.
 * States the full truth (cadence + window) in one sentence (§E). Edit opens a
 * Praetorian-only 2-step wizard (OPEN-Q-1 option a). Customers/non-Praetorian
 * see a read-only pill and CANNOT open the wizard.
 */
export const ScanScheduleCard: React.FC<ScanScheduleCardProps> = ({
  email,
  editable = true,
}) => {
  const { isPraetorianUser } = useAuth();
  const { canManageSettings } = useEntitlements();
  const [wizardOpen, setWizardOpen] = useState(false);

  const { currentCadence, isSet: cadenceIsSet } = useScanCadence({ email });
  const { currentScanSchedule } = useScanSchedule({ email });

  // Praetorian-only edit for the whole card (B1/B3, OPEN-Q-1 option a).
  const canEdit = editable && isPraetorianUser && canManageSettings;

  // "Set" requires BOTH a cadence and a schedule window; otherwise A7 default.
  // (useScanSchedule always returns a non-null object in production; guard
  // defensively so a missing window never crashes the card.)
  const hasSchedule =
    (currentScanSchedule?.days?.length ?? 0) > 0 &&
    !!currentScanSchedule?.start &&
    !!currentScanSchedule?.end;
  const isSet = cadenceIsSet && hasSchedule;

  const sentence =
    isSet && currentScanSchedule
      ? buildSentence({
          frequency: currentCadence.frequency,
          days: currentScanSchedule.days,
          start: currentScanSchedule.start as string,
          end: currentScanSchedule.end as string,
        })
      : DEFAULT_SENTENCE;

  return (
    <>
      <SettingCompactCard data-testid="scan-schedule-card">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h4 className="text-chariot-text-primary font-bold">
              Scan Schedule
            </h4>
            {!isSet && (
              <span className="bg-chariot-layer2 text-chariot-status-info rounded-full px-2 py-0.5 text-xs font-bold tracking-wide uppercase">
                Default
              </span>
            )}
          </div>
          <SentenceText
            parts={sentence}
            data-testid="scan-schedule-card-sentence"
            className={cn(
              'text-sm',
              isSet
                ? 'text-chariot-text-primary'
                : 'text-chariot-text-secondary'
            )}
          />
          <p className="text-chariot-text-secondary text-xs">
            How often base scans recur, and the weekly window when they may
            launch (UTC).
          </p>
        </div>

        {canEdit ? (
          <div className="absolute top-1/2 right-4 -translate-y-1/2">
            <Button
              onClick={() => setWizardOpen(true)}
              variant="ghost"
              size="sm"
              data-testid="scan-schedule-edit-button"
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="absolute top-1/2 right-4 -translate-y-1/2">
            <span
              className="border-chariot-stroke text-chariot-text-secondary inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold"
              data-testid="scan-schedule-readonly-pill"
            >
              <LockIcon className="size-3" />
              Read-only
            </span>
          </div>
        )}
      </SettingCompactCard>

      {canEdit && wizardOpen && (
        <ScanScheduleWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          email={email}
          initialFrequency={currentCadence.frequency}
          initialManualBypass={currentScanSchedule?.manualBypass ?? false}
        />
      )}
    </>
  );
};
